/**
 * Jensen Home – Black Friday (VIP goud)
 * <jensen-bf-live>      ververst zijn inhoud bij een variantwissel (zelfde aanpak als product-price.js)
 * <jensen-bf-countdown> telt af naar data-end (Nederlandse tijd) of elke dag naar middernacht
 */
const standardEvents = import('@shopify/events')
  .then((m) => m.StandardEvents)
  .catch(() => null);

function amsterdamEpoch(y, mo, d, h, mi) {
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Amsterdam',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date(guess));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
    return guess - (wall - guess);
  } catch (e) {
    return guess - 3600000;
  }
}

function parseEnd(value) {
  const str = String(value || '').trim();
  let m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T]+(\d{1,2})[:.](\d{2}))?/);
  if (m) return amsterdamEpoch(+m[1], +m[2], +m[3], m[4] ? +m[4] : 23, m[5] ? +m[5] : 59);
  m = str.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})(?:[ T]+(\d{1,2})[:.](\d{2}))?/);
  if (m) return amsterdamEpoch(+m[3], +m[2], +m[1], m[4] ? +m[4] : 23, m[5] ? +m[5] : 59);
  return null;
}

function syncTokens(html) {
  const data = html.querySelector('[data-jbf-data]');
  if (!data) return;
  const hasDiscount = data.dataset.hasDiscount === 'true';
  document.querySelectorAll('[data-jbf-token="saving"]').forEach((el) => {
    el.textContent = data.dataset.saving || '';
  });
  document.querySelectorAll('[data-jbf-token="pct"]').forEach((el) => {
    el.textContent = data.dataset.pct || '';
  });
  document.querySelectorAll('[data-jbf-needs-discount]').forEach((el) => {
    el.hidden = !hasDiscount;
  });
}

class JensenBfLive extends HTMLElement {
  #scope = null;
  #eventName = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    if (!this.#scope) return;
    standardEvents.then((events) => {
      if (!events?.productSelect || !this.isConnected || this.#eventName) return;
      this.#eventName = events.productSelect;
      this.#scope.addEventListener(this.#eventName, this.#onSelect);
    });
  }

  disconnectedCallback() {
    if (this.#scope && this.#eventName) this.#scope.removeEventListener(this.#eventName, this.#onSelect);
    this.#eventName = null;
  }

  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;
    promise
      .then((result) => {
        const html = result?.detail?.html;
        if (!html || typeof html.querySelector !== 'function') return;
        const raw = this.dataset.jbfKey || '';
        const key = window.CSS?.escape ? window.CSS.escape(raw) : raw;
        const fresh = html.querySelector(`jensen-bf-live[data-jbf-key="${key}"]`);
        if (fresh) {
          if (fresh.className !== this.className) this.className = fresh.className;
          if (fresh.innerHTML !== this.innerHTML) this.innerHTML = fresh.innerHTML;
        }
        syncTokens(html);
      })
      .catch(() => {});
  };
}

class JensenBfCountdown extends HTMLElement {
  #timer = null;
  #end = null;

  connectedCallback() {
    const host = this.#host();
    const daily = this.dataset.mode === 'daily';
    this.#end = daily ? null : parseEnd(this.dataset.end);
    if (!daily && this.#end === null) {
      host.classList.add('is-invalid');
      return;
    }
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #host() {
    return this.closest('[data-jbf-cd-host]') || this;
  }

  #target() {
    if (this.dataset.mode === 'daily') {
      const t = new Date();
      t.setHours(24, 0, 0, 0);
      return t.getTime();
    }
    return this.#end;
  }

  #tick() {
    const host = this.#host();
    let s = Math.floor((this.#target() - Date.now()) / 1000);
    if (s <= 0) {
      window.clearInterval(this.#timer);
      host.classList.remove('is-running');
      host.classList.add('is-ended');
      return;
    }
    host.classList.add('is-running');
    const d = Math.floor(s / 86400);
    s -= d * 86400;
    const h = Math.floor(s / 3600);
    s -= h * 3600;
    const m = Math.floor(s / 60);
    const sec = s - m * 60;
    const pad = (n) => String(n).padStart(2, '0');
    this.#set('d', String(d));
    this.#set('h', pad(h));
    this.#set('m', pad(m));
    this.#set('s', pad(sec));
    this.toggleAttribute('data-no-days', d === 0);
  }

  #set(unit, value) {
    const el = this.querySelector(`[data-u="${unit}"]`);
    if (el && el.textContent !== value) el.textContent = value;
  }
}

if (!customElements.get('jensen-bf-live')) customElements.define('jensen-bf-live', JensenBfLive);
if (!customElements.get('jensen-bf-countdown')) customElements.define('jensen-bf-countdown', JensenBfCountdown);
